import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {createGrant} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    DEFAULT_GRANT,

    GRANT_DESCRIPTION_CONTROL,
    GRANT_DESCRIPTION_REQUIRED_ERROR,
    GRANT_DESCRIPTION_REQUIRED_MESSAGE,

    GRANT_EXTERNAL_ID_CONTROL,
    GRANT_EXTERNAL_ID_REQUIRED_ERROR,
    GRANT_EXTERNAL_ID_REQUIRED_MESSAGE,

    GRANT_NAME_CONTROL,
    GRANT_NAME_REQUIRED_ERROR,
    GRANT_NAME_REQUIRED_MESSAGE,
} from './constants';

import type {Grant} from 'src/openapi/admin/models/Grant';

export const useCreateGrant = () => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(() => {
        const payload: {success: true, error: false, data: Grant} = {
            success: true,
            error: false,
            data: DEFAULT_GRANT,
        };
        return Promise.resolve({type: '', payload});
    }, []);

    const saveDispatchAction = useCallback((grant: Grant, signal: AbortSignal) => apiDispatch(
        createGrant({grant}, {signal}),
    ), [apiDispatch]);

    const editValidation = useCallback((data: Grant) => {
        if (data.id) {
            removeError(GRANT_EXTERNAL_ID_CONTROL);
        }

        if (data.name) {
            removeError(GRANT_NAME_CONTROL);
        }

        if (data.description) {
            removeError(GRANT_DESCRIPTION_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: Grant) => {
        let valid = true;

        if (!data.id) {
            addError({
                controlId: GRANT_EXTERNAL_ID_CONTROL,
                code: GRANT_EXTERNAL_ID_REQUIRED_ERROR,
                message: GRANT_EXTERNAL_ID_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.name) {
            addError({
                controlId: GRANT_NAME_CONTROL,
                code: GRANT_NAME_REQUIRED_ERROR,
                message: GRANT_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.description) {
            addError({
                controlId: GRANT_DESCRIPTION_CONTROL,
                code: GRANT_DESCRIPTION_REQUIRED_ERROR,
                message: GRANT_DESCRIPTION_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    return useCommonDataHook({
        initValue: DEFAULT_GRANT,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
